export const API_SITE = 'WS0005';
export const SESSION_KEY = 'ninet6_player_session';
export const DEFAULT_LIMIT = 20;
export const DATE_PRESETS = Object.freeze(['today', 'yesterday', 'last7']);
export const DATE_PRESET_LABEL = Object.freeze({
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
});

export function apiBase() {
  return `/api/site/${encodeURIComponent(API_SITE)}/v1`;
}

export function accessToken() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.accessToken || '';
  } catch {
    return '';
  }
}

export function isoDay(value = new Date()) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function datePresetRange(preset, now = new Date()) {
  const key = DATE_PRESETS.includes(preset) ? preset : 'last7';
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (key === 'today') {
    return { preset: key, from: isoDay(today), to: isoDay(today) };
  }
  if (key === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { preset: key, from: isoDay(y), to: isoDay(y) };
  }
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { preset: 'last7', from: isoDay(from), to: isoDay(today) };
}

function queryString(params) {
  const t = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === '' || value === 'All') return;
    t.set(key, String(value));
  });
  const q = t.toString();
  return q ? `?${q}` : '';
}

async function playerGet(path) {
  const token = accessToken();
  const res = await fetch(`${apiBase()}${path}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json && json.data !== undefined ? json.data : json;
}

export async function fetchHistoryFilters() {
  return playerGet('/history/filters');
}

export async function fetchHistoryRecords(params) {
  return playerGet(`/history/records${queryString(params)}`);
}

export async function fetchWcktBets(params) {
  return playerGet(`/history/9wicket-bets${queryString(params)}`);
}

export async function fetchPublicCategories() {
  const data = await playerGet('/public/categories');
  return Array.isArray(data) ? data : [];
}

export async function fetchPublicProviders(params) {
  const data = await playerGet(`/public/providers${queryString(params)}`);
  return Array.isArray(data) ? data : [];
}

export function isBlank(value) {
  if (value == null) return true;
  const text = String(value).trim();
  return !text || text === '-' || text === '—';
}

export function isSportsProvider(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text || text === 'all') return false;
  return (
    text === '9wicket'
    || text === '9wickets'
    || text === 'wckt'
    || text === '9wicket sports'
    || text === 'igaming 9wicket'
  );
}

export function shouldFetchSports(provider) {
  return !provider || provider === 'All' || isSportsProvider(provider);
}

export function shouldFetchGames(provider) {
  return !provider || provider === 'All' || !isSportsProvider(provider);
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isOpenSportsRow(row) {
  return /^open$/i.test(String(row?.settlementStatus || ''));
}

export function isSettledSportsRow(row) {
  return /^settled$/i.test(String(row?.settlementStatus || ''));
}

export function isUnsettledGameRow(row) {
  if (String(row?.result || '').toUpperCase() !== 'BET') return false;
  const win = num(row?.winAmount);
  const net = num(row?.netAmount);
  const loss = num(row?.lossAmount);
  if (win && win > 0) return false;
  if (net && net !== 0) return false;
  if (loss && loss > 0) return false;
  return true;
}

export function isSettledGameRow(row) {
  const result = String(row?.result || '').toUpperCase();
  return result === 'WIN' || result === 'LOSS' || result === 'REFUND';
}

export function formatMoney(value) {
  const n = num(value);
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}৳ ${Math.abs(n).toLocaleString('en-US')}`;
}

export function profitClass(value) {
  const n = num(value);
  if (n == null || n === 0) return '';
  return n > 0 ? 'br-amt-pos' : 'br-amt-neg';
}

export function rowTime(row) {
  return row?.bettingDate || row?.createdAt || '';
}

function dash(value) {
  if (isBlank(value)) return '';
  return String(value).trim();
}

function eventGameName(value) {
  const text = dash(value);
  if (!text || /^round\s+\S+$/i.test(text)) return '';
  return text;
}

function displayGameName(row) {
  const name = dash(row?.gameName);
  const code = dash(row?.gameCode);
  if (!name) return '';
  if (code && name === code) return '';
  if (/^\d+$/.test(name)) return '';
  return name;
}

export function presentSportsRow(row) {
  return {
    key: `wckt:${dash(row?.id) || rowTime(row)}`,
    source: '9wicket',
    platform: dash(row?.provider),
    gameType: dash(row?.category) || dash(row?.gameType),
    game: displayGameName(row) || eventGameName(row?.eventName),
    betLabel: dash(row?.betLabel),
    betDetail: dash(row?.betDetail),
    turnover: num(row?.validBet) ?? num(row?.betAmount),
    profitLoss: num(row?.winLossAmount),
    betAmount: num(row?.betAmount),
    result: dash(row?.result),
    status: dash(row?.settlementStatus),
    referenceId: dash(row?.id),
    at: rowTime(row),
  };
}

export function presentGameRow(row) {
  return {
    key: `game:${dash(row?.id) || dash(row?.referenceId) || rowTime(row)}`,
    source: 'game',
    platform: dash(row?.provider),
    gameType: dash(row?.category),
    game: displayGameName(row),
    turnover: num(row?.betAmount),
    profitLoss: num(row?.netAmount),
    betAmount: num(row?.betAmount),
    result: dash(row?.result),
    status: dash(row?.status),
    referenceId: dash(row?.referenceId) || dash(row?.id),
    at: rowTime(row),
  };
}

export function displayCell(value) {
  if (isBlank(value) && value !== 0) return '—';
  return String(value);
}

export function sortBettingRows(rows) {
  return (rows || []).slice().sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}

export function combineSourcePages(sportsPage, gamePage) {
  const sports = sportsPage && typeof sportsPage === 'object' ? sportsPage : {};
  const games = gamePage && typeof gamePage === 'object' ? gamePage : {};
  const sportsPages = Math.max(1, Number(sports.totalPages) || 1);
  const gamePages = Math.max(1, Number(games.totalPages) || 1);
  return {
    totalPages: Math.max(sportsPages, gamePages),
    sportsTotal: Number(sports.total) || 0,
    gameTotal: Number(games.total) || 0,
  };
}

export function filterChipOptions(list) {
  return (Array.isArray(list) ? list : [])
    .map((item) => (typeof item === 'string' ? { value: item, label: item } : item))
    .filter((item) => item && item.value && item.value !== 'All');
}

function filterKey(value) {
  return String(value || '').trim().toLowerCase();
}

function flagText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function flagIsInactive(value) {
  if (value === false || value === 0) return true;
  const text = flagText(value);
  return text === 'inactive' || text === 'false' || text === '0' || text === 'off' || text === 'no' || text === 'disabled';
}

function flagIsActive(value) {
  if (value === true || value === 1) return true;
  const text = flagText(value);
  return text === 'active' || text === 'true' || text === '1' || text === 'on' || text === 'yes';
}

export function categoryIsActive(cat) {
  if (!cat || typeof cat !== 'object') return false;
  if (flagIsInactive(cat.status) || flagIsInactive(cat.enabled)) return false;
  if (cat.status != null && String(cat.status).trim() !== '') return flagIsActive(cat.status);
  if (cat.enabled != null && cat.enabled !== '') return flagIsActive(cat.enabled);
  return true;
}

export function providerIsActive(row) {
  if (!row || typeof row !== 'object') return false;
  if (flagIsInactive(row.status) || flagIsInactive(row.enabled)) return false;
  if (row.status != null && String(row.status).trim() !== '') return flagIsActive(row.status);
  if (row.enabled != null && row.enabled !== '') return flagIsActive(row.enabled);
  return true;
}

export function matchPublicCategory(historyCategory, publicCategories) {
  const want = filterKey(historyCategory);
  if (!want || want === 'all') return null;
  return (Array.isArray(publicCategories) ? publicCategories : []).find((cat) => {
    if (!categoryIsActive(cat) || isHotFilterCategory(cat)) return false;
    return filterKey(cat.name || cat.label) === want || filterKey(cat.id) === want;
  }) || null;
}

export function canonicalProviderChip(item = {}) {
  const label = String(item.label || item.providerName || item.value || item.providerCode || '').trim();
  const value = String(item.providerName || item.label || item.value || label).trim();
  if (!label && !value) return null;
  return { value: value || label, label: label || value };
}

export function isHotFilterCategory(cat) {
  if (!cat || typeof cat !== 'object') return false;
  if (cat.isHot) return true;
  const id = filterKey(cat.id || cat.panelId);
  if (id === 'popular' || id === 'cat_hot' || id === 'hot') return true;
  const name = filterKey(cat.name || cat.label);
  return name === 'hot' || name === 'hot game' || name === 'hot games';
}

export function websiteCategoryChips(categories) {
  return (Array.isArray(categories) ? categories : [])
    .filter((cat) => categoryIsActive(cat) && !isHotFilterCategory(cat))
    .map((cat) => {
      const label = String(cat.name || cat.label || '').trim();
      const id = String(cat.id || '').trim();
      if (!label || !id || filterKey(label) === 'all') return null;
      return { value: label, label, id };
    })
    .filter(Boolean);
}

export function uniqueProviderChips(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const chip = canonicalProviderChip(item);
    if (!chip || !chip.value || chip.value === 'All') return;
    const key = filterKey(chip.value);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ value: chip.value, label: chip.label || chip.value });
  });
  out.sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
  return out;
}

export function catalogProviderChips(rows) {
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!providerIsActive(row)) return;
    const namedMembers = Array.isArray(row.members)
      ? row.members.filter((member) => {
        if (!providerIsActive(member)) return false;
        return String(member.providerName || member.label || member.value || '').trim() !== '';
      })
      : [];
    const sources = namedMembers.length ? namedMembers : [row];
    sources.forEach((item) => {
      const chip = canonicalProviderChip(item);
      if (chip) out.push(chip);
    });
  });
  return uniqueProviderChips(out);
}

export function matchesHistoryCategory(row, gameType) {
  const key = String(gameType || 'All').trim() || 'All';
  if (key === 'All') return true;
  return filterKey(row && row.gameType) === filterKey(key);
}

export function categoryIncludesSportsProvider(gameType, providersByGameType = {}) {
  const key = String(gameType || 'All').trim() || 'All';
  if (key === 'All') return true;
  const list = providersByGameType && providersByGameType[key];
  if (!Array.isArray(list)) return false;
  return list.some((item) => isSportsProvider(item && item.value) || isSportsProvider(item && item.label));
}

export function providersForGameType(gameType, providersByGameType = {}) {
  const key = String(gameType || 'All').trim() || 'All';
  if (key === 'All') {
    if (Array.isArray(providersByGameType && providersByGameType.All)) {
      return uniqueProviderChips(providersByGameType.All);
    }
    return uniqueProviderChips(Object.values(providersByGameType || {}).flat());
  }
  const scoped = providersByGameType && providersByGameType[key];
  return Array.isArray(scoped) ? uniqueProviderChips(scoped) : [];
}

export function retainProviderIfAvailable(provider, available) {
  if (!provider || provider === 'All') return 'All';
  return (available || []).some((item) => item.value === provider) ? provider : 'All';
}

export async function buildProvidersByGameType(publicCategories) {
  const cats = websiteCategoryChips(publicCategories);
  const map = {};
  await Promise.all(cats.map(async (cat) => {
    const rows = await fetchPublicProviders({ category: cat.id }).catch(() => []);
    map[cat.value] = catalogProviderChips(rows);
  }));
  map.All = uniqueProviderChips(Object.values(map).flat());
  return map;
}
